import { WORK_PERMIT_STATUS } from '../consts'
import { createResolvedHistoryCommentContent } from './contracts'

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether a history text value contains visible non-whitespace content.
 * @param {*} v
 * @returns {boolean}
 */
export function hasVisibleHistoryText (v) {
  return v != null && String(v).trim() !== ''
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Resolve raw history detail into the final UI comment display contract.
 * @param {string} status
 * @param {Object} detail
 * @param {string} emptyValue
 * @returns {import('./contracts').ResolvedHistoryCommentContent}
 */
export function resolveCommentContent (status, detail = {}, emptyValue = '--') {
  if (status === WORK_PERMIT_STATUS.APPR) {
    if (detail.leaveExternalComments && detail.externalComments) {
      return createResolvedHistoryCommentContent({ content: '', externalContent: detail.externalComments })
    }
    if (hasVisibleHistoryText(detail.comments)) {
      return createResolvedHistoryCommentContent({ content: '', externalContent: detail.comments })
    }
    return createResolvedHistoryCommentContent({ content: '', externalContent: null })
  }

  if (status === WORK_PERMIT_STATUS.REJECTED) {
    const internal = detail.comments
    const external = detail.externalComments
    const showExternal = detail.leaveExternalComments && hasVisibleHistoryText(external)
    const showInternal = hasVisibleHistoryText(internal)
    if (showExternal && showInternal) {
      return createResolvedHistoryCommentContent({ content: internal, externalContent: external })
    }
    if (showExternal) {
      return createResolvedHistoryCommentContent({ content: emptyValue, externalContent: external })
    }
    if (showInternal) return createResolvedHistoryCommentContent({ content: '', externalContent: internal })
    return createResolvedHistoryCommentContent({ content: '', externalContent: null })
  }

  return createResolvedHistoryCommentContent({
    content: detail.comments || emptyValue,
    externalContent: (detail.leaveExternalComments && detail.externalComments)
      ? detail.externalComments
      : null,
  })
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether a display row status is a final approver or rejected row.
 * @param {Object} item
 * @returns {boolean}
 */
export function historyRowIsApprOrReject (item) {
  return item.status === WORK_PERMIT_STATUS.APPR || item.status === WORK_PERMIT_STATUS.REJECTED
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether rendered history text should count as meaningful UI content.
 * @param {*} v
 * @param {string} [emptyValue]
 * @returns {boolean}
 */
export function historyTextMeaningful (v, emptyValue = '--') {
  if (v == null) return false
  const s = String(v).trim()
  if (s === '') return false
  if (s === String(emptyValue).trim()) return false
  return true
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Resolve the single body text used by approver/final history rows.
 * @param {Object} item
 * @param {string} [emptyValue]
 * @returns {string|null}
 */
export function approverHistoryBodyText (item, emptyValue = '--') {
  const ext = item.externalContent
  if (ext != null && String(ext).trim() !== '') return ext
  const c = item.content
  if (c == null) return null
  const s = String(c).trim()
  if (s === '' || s === String(emptyValue).trim()) return null
  return c
}
